import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PlayCircle, Phone, ArrowRight, Brain, User, Building2 } from 'lucide-react';

const SCENARIOS = [
  { value: 'sales', label: 'Customer asks for Sales', route: 'Sales Department → Ext 100', ai: false },
  { value: 'support', label: 'Customer asks for Support', route: 'Support Department → Ext 200', ai: false },
  { value: 'extension', label: 'Caller dials extension 101', route: 'Direct → John Smith (Ext 101)', ai: false },
  { value: 'name', label: 'Caller says "John Smith"', route: 'Directory Match → John Smith → +1(555)123-4567', ai: false },
  { value: 'ai_faq', label: 'Customer asks about hours', route: 'AI handles → answers from FAQ', ai: true },
  { value: 'ai_escalate', label: 'Customer wants to speak to manager', route: 'AI → Escalate → Manager Dept', ai: true },
  { value: 'no_match', label: 'No match / fallback', route: 'Fallback → Operator / Main Line', ai: false },
  { value: 'after_hours', label: 'Call during after hours', route: 'After-hours message → Voicemail', ai: false },
];

export default function VDTestIVR() {
  const [selected, setSelected] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const runTest = () => {
    if (!selected) return;
    setRunning(true);
    setResult(null);
    setTimeout(() => {
      const scenario = SCENARIOS.find(s => s.value === selected);
      setResult(scenario);
      setRunning(false);
    }, 1200);
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="vd-test-ivr">
      <div>
        <h1 className="text-2xl font-bold">Test IVR</h1>
        <p className="text-muted-foreground text-sm mt-1">Simulate call scenarios to verify your routing</p>
      </div>

      <Card className="bg-card/5 border-white/10">
        <CardHeader><CardTitle className="text-base">Call Simulator</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="bg-card/5 border-white/10"><SelectValue placeholder="Choose a scenario..." /></SelectTrigger>
            <SelectContent className="bg-[#1a1d2e] border-white/10 text-foreground">
              {SCENARIOS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={runTest} disabled={!selected || running} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium">
            <PlayCircle className="w-4 h-4 mr-1.5" />{running ? 'Simulating...' : 'Run Test'}
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="bg-card/5 border-white/10">
          <CardHeader><CardTitle className="text-base">Result</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <Badge className="bg-blue-500/20 text-blue-300">Scenario</Badge>
              <span>{result.label}</span>
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-card/5">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-foreground">Incoming Call</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              {result.ai ? <Brain className="w-4 h-4 text-purple-400" /> : <Building2 className="w-4 h-4 text-emerald-400" />}
              <span className="text-sm text-emerald-300">{result.route}</span>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">Routed Successfully</Badge>
              {result.ai && <Badge variant="outline" className="border-purple-500/30 text-purple-400">AI Handled</Badge>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
